/**
 * ImageUpload — переиспользуемый UI загрузки/превью/удаления изображения вопроса.
 *
 * Извлечён из HostEditor.tsx (Фаза 8) для совместного использования хостом
 * (редактор квизов) и админкой (редактор витрин). Presentation-компонент: не
 * зависит от состояния экрана; вызывает `uploadImage` из api.ts (общий API-вызов
 * POST /api/uploads под auth-сессией) и сообщает о новом URL через `onImageChange`.
 * Перед отправкой файл проходит клиентское пресжатие через Canvas
 * (`imagepreprocess.preprocessImage`, Фаза 12 A2): downscale до ~1600 px и
 * WebP/JPEG ~0.85 — закрывает 413 на сырых фото 3–8 МиБ и трафик пользователя;
 * контракт api.ts не меняется (серверный конвейер остаётся авторитетным).
 *
 * Устойчивость к истечению подписи превью (secure_link в проде): подписанный URL
 * может протухнуть или предсуществующий «голый» путь может отдать 403 → onError
 * (state `imgBroken`) убирает сломанный <img>, не роняя превью; кнопки
 * «Заменить»/«Убрать» остаются рабочими.
 *
 * data-testid (`q-image-*`) сохранены 1:1 — на них завязаны тесты HostEditor.test.tsx.
 */
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, uploadImage } from '../../api';
import { preprocessImage } from '../../imagepreprocess';
import { errorMessage } from '../../util';

export interface ImageUploadProps {
  /** Текущий URL изображения вопроса; undefined/пусто — изображения нет. */
  image?: string;
  /** alt для превью (обычно текст вопроса). */
  alt: string;
  /**
   * Новый URL после успешной загрузки ИЛИ пустая строка при удалении. Caller сам
   * решает, как сохранить (кладёт в DraftQuestion.image, пусто → undefined).
   */
  onImageChange: (url: string) => void;
}

/**
 * UI загрузки/превью/удаления изображения вопроса. POST /api/uploads через
 * uploadImage; на 400/413/422/429 показывает дружелюбное i18n-сообщение (маппинг
 * статусов ApiError). Превью — <img src={image}>; «Убрать изображение» очищает
 * поле (onImageChange('')). Состояние загрузки блокирует кнопки и показывает «Загрузка…».
 */
export function ImageUpload({ image, alt, onImageChange }: ImageUploadProps): JSX.Element {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // Пресжатие до uploadImage (A2): never-throw — сбой ⇒ исходный файл.
      const processed = await preprocessImage(file);
      const url = await uploadImage(processed);
      onImageChange(url);
    } catch (e) {
      if (e instanceof ApiError) {
        switch (e.status) {
          case 413:
            setError(t('errors.imageTooLarge'));
            break;
          case 422:
            setError(t('errors.imageTooLargeAfterCompression'));
            break;
          case 400:
            setError(t('errors.imageInvalid'));
            break;
          case 429:
            setError(t('errors.imageRateLimit'));
            break;
          default:
            setError(e.message);
        }
      } else {
        setError(errorMessage(e));
      }
    } finally {
      setUploading(false);
    }
  }

  function handleRemove(): void {
    setError(null);
    onImageChange('');
  }

  const hasImage = typeof image === 'string' && image.length > 0;

  // Устойчивость к истечению/невалидности подписи превью (Фаза 6, И2): подписанный
  // URL может протухнуть → onError убирает сломанный <img>, не роняя превью (кнопки
  // «Заменить/Убрать» остаются — хост/админ может перезагрузить картинку). Сброс при смене.
  const [imgBroken, setImgBroken] = useState(false);
  useEffect(() => {
    setImgBroken(false);
  }, [image]);

  return (
    <div className="q-image-upload" data-testid="q-image-upload">
      <span className="field-label">{t('host.editor.imageLabel')}</span>
      {hasImage ? (
        <div className="q-image-upload__preview">
          {imgBroken ? (
            <span className="muted small" data-testid="q-image-broken" role="status" title={alt}>
              {t('host.editor.imageBroken')}
            </span>
          ) : (
            <img
              className="q-image-upload__img"
              src={image}
              alt={alt}
              data-testid="q-image-thumb"
              onError={() => setImgBroken(true)}
            />
          )}
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {t('host.editor.replaceImage')}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleRemove}
              disabled={uploading}
              data-testid="q-image-remove"
            >
              {t('host.editor.removeImage')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="q-image-choose"
        >
          {t('host.editor.chooseImage')}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden-file"
        onChange={(e) => void handleFile(e)}
        data-testid="q-image-input"
      />
      {uploading ? (
        <span className="muted small" role="status" data-testid="q-image-loading">
          {t('host.editor.imageUploading')}
        </span>
      ) : null}
      {error ? (
        <p className="error" role="alert" data-testid="q-image-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
